/**
 * What the form says about a worked-out field: the currency its answer is in,
 * and the line under it that says how it is worked out.
 *
 * Salon-Three-Services draws the Bill as "₹875" with "Worked out · the total of
 * Price over Services" under it. A record that totals nothing carries no
 * currency of its own (the Visits record counts entries, not money), so a money
 * answer there falls back to the business's currency rather than drawing a
 * bare number nobody can tell is an amount.
 */
import {
  formatMoney,
  formatNumber,
  formulaOf,
  formulaSentence,
  type FormulaHopRow,
  type RecordSchemaField,
  type RecordSchemaResponse,
  workedResultOf,
} from '@alluzo/shared';

import { wordLookupOf } from './formula-draft';
import { t, tData, tOr } from './i18n';
import { lineRefFields } from './line-above';

/** The currency a worked-out field's answer is drawn in, or null when it is not money. A rollup's total counts: the Room bill is an amount. */
export const formulaCurrencyOf = (
  field: RecordSchemaField,
  recordCurrency: string | null,
  businessCurrency: string | null,
): string | null => {
  if (workedResultOf(field.type, field.spec) !== 'money') return null;
  return recordCurrency ?? businessCurrency;
};

/**
 * "Worked out · the total of Price over Services", or null for a field that is
 * not worked out. `readSchema` answers the lists the formula reaches, from the
 * phone's cache; a list it cannot answer says its field the way the builder
 * does, rather than hiding the line.
 */
export const workedOutLine = (
  source: RecordSchemaResponse,
  field: RecordSchemaField,
  readSchema: (recordId: string) => RecordSchemaResponse | null,
  above?: Readonly<{ parent: RecordSchemaResponse; root: RecordSchemaResponse }>,
): string | null => {
  const formula = formulaOf(field.spec);
  if (formula === null) return null;
  const targets = new Map<string, RecordSchemaResponse>();
  for (const one of source.fields) {
    if (one.targetRecordId === null || targets.has(one.targetRecordId)) continue;
    const target = readSchema(one.targetRecordId);
    if (target !== null) targets.set(one.targetRecordId, target);
  }
  const words = wordLookupOf({ source, targets });
  const sentence = formulaSentence(formula.expr, above === undefined ? words : { ...words, hop: hopUp(source, above, words.hop) }, tData);
  return sentence === '' ? null : t('entry.worked_out_line', { sentence });
};

/** The noun keys every record or line is born with: a record that still says one has no noun of its own. */
const BORN_WITH: ReadonlySet<string> = new Set(['noun.entry', 'noun.line']);

/**
 * The row a hop up the tree lands on, as the sentence names it: the record's
 * noun for one entry when it has one of its own (the owner's word, or a
 * template's), else the record's name — "of the stay", "of E2E Stays".
 */
const hopRowOf = (row: RecordSchemaResponse): FormulaHopRow => {
  const noun = row.entryNoun?.one ?? (BORN_WITH.has(row.entryNounKey) ? null : tOr(row.entryNounKey, '', { count: 1 }));
  return noun === null || noun === '' ? { name: row.name } : { noun: noun.toLocaleLowerCase() };
};

/**
 * A line's hop up the tree, in words (Decision 6 of the lines plan). A line
 * schema leaves its parent and root fields out, so the ordinary lookup cannot
 * name them: a hop through either reads its field with the row it lands on —
 * "Check in of the stay" — from the schema of that row.
 */
const hopUp = (
  source: RecordSchemaResponse,
  above: Readonly<{ parent: RecordSchemaResponse; root: RecordSchemaResponse }>,
  fallback: (via: string, fieldId: string) => { via: string; field: string } | null,
) => (via: string, fieldId: string): { via: string; field: string; row?: FormulaHopRow } | null => {
  const refs = lineRefFields(source);
  const row = via === refs.parent ? above.parent : via === refs.root ? above.root : null;
  if (row === null) return fallback(via, fieldId);
  const label = row.fields.find((one) => one.id === fieldId)?.label;
  if (label === undefined) return null;
  const named = hopRowOf(row);
  return { via: 'noun' in named ? named.noun : named.name, field: label, row: named };
};

/**
 * A worked-out value as the list card draws it, unsigned: "₹875" for a money
 * answer by the same currency rule as the form (`formulaCurrencyOf`), else the
 * digits between the affixes `value.readonly` declares. A value that is not a
 * number is drawn as it is.
 */
export const computedWordOf = (
  field: RecordSchemaField,
  value: number,
  affixes: { readonly prefix: string; readonly suffix: string },
  recordCurrency: string | null,
  businessCurrency: string | null,
): string => {
  const currency = formulaCurrencyOf(field, recordCurrency, businessCurrency);
  // The currency mark is the amount's own; a prefix would draw it twice.
  if (currency !== null) return `${formatMoney(value, currency)}${affixes.suffix}`;
  return `${affixes.prefix}${formatNumber(value)}${affixes.suffix}`;
};

/**
 * A worked-out value on a proposal card, which the server keys by the field's
 * label: an amount by the form's currency rule (`formulaCurrencyOf`), so the
 * card reads ₹700 where the form does; any other number grouped; nothing a
 * dash; words as sent. A label this phone's schema does not hold keeps its
 * digits.
 */
export const proposedWorkedOutOf = (
  schema: RecordSchemaResponse | null,
  label: string,
  value: string | number | boolean | null,
  recordCurrency: string | null,
  businessCurrency: string | null,
): string => {
  if (value === null) return '—';
  if (typeof value !== 'number') return String(value);
  const field = schema?.fields.find((one) => one.type === 'formula' && one.label === label);
  const currency = field === undefined ? null : formulaCurrencyOf(field, recordCurrency, businessCurrency);
  return currency === null ? formatNumber(value) : formatMoney(value, currency);
};
